import React, { useState, useEffect, useRef } from 'react';
import { Loader2, RefreshCw, Building, Database, FileText, Trash2, X, Check, Download, Map as MapIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

export default function SitemapTab({ isDark, userInfo, selectedMunicipalityId, municipalitiesList }) {
  // -- State: Sitemap --
  const [sitemapStatus, setSitemapStatus] = useState(null);
  const [loadingSitemapStatus, setLoadingSitemapStatus] = useState(false);
  const [checkingSitemap, setCheckingSitemap] = useState(false);
  const [syncingSitemap, setSyncingSitemap] = useState(false);
  const [selectedMunicipalityForSitemap, setSelectedMunicipalityForSitemap] = useState(null);
  const [sitemapConfig, setSitemapConfig] = useState({
    sitemap_url: '',
    sitemap_sync_config: {
      enabled: false,
      check_interval_minutes: 60,
      daily_sync_time: '03:00'
    }
  });
  const [loadingSitemapConfig, setLoadingSitemapConfig] = useState(false);
  const [savingSitemapConfig, setSavingSitemapConfig] = useState(false);

  // -- State: BAY:entry ingestion (Provider Admin, Sitemap tab) --
  const [bayEntryJobId, setBayEntryJobId] = useState(null);
  const [bayEntryStatus, setBayEntryStatus] = useState(null);
  const [bayEntryRunning, setBayEntryRunning] = useState(false);
  const [bayEntryReportExpanded, setBayEntryReportExpanded] = useState(false);
  const bayEntryPollIntervalRef = useRef(null);

  // -- State: Reindex/Purge (Provider Admin) --
  const [reindexJob, setReindexJob] = useState(null);
  const [reindexRunning, setReindexRunning] = useState(false);
  const [reindexForm, setReindexForm] = useState({
    exclude_events_before_year: new Date().getFullYear(),
    deactivate_old_chunks: true,
    max_urls: null,
    showAdvanced: false
  });
  const [purgeRunning, setPurgeRunning] = useState(false);
  const [purgeForm, setPurgeForm] = useState({
    older_than_days: 30
  });
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [retentionStatus, setRetentionStatus] = useState(null);

  // -- State: Index Health (Provider Admin) --
  const [indexHealth, setIndexHealth] = useState(null);
  const [loadingIndexHealth, setLoadingIndexHealth] = useState(false);
  // -- State: Dirty URLs (Provider Admin) --
  const [dirtyReport, setDirtyReport] = useState(null);
  const [loadingDirtyReport, setLoadingDirtyReport] = useState(false);
  const [reingestRunning, setReingestRunning] = useState(false);
  const [reingestJob, setReingestJob] = useState(null);

  // -- Sitemap Functions --
  const fetchSitemapStatus = async (municipalityId = null) => {
    try {
      setLoadingSitemapStatus(true);
      const params = municipalityId ? { municipality_id: municipalityId } : {};
      const response = await apiClient.get('/sitemap/status', { params });
      setSitemapStatus(response.data);
    } catch (error) {
      console.error('Error fetching sitemap status:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Fehler beim Laden';
      toast.error(`Fehler beim Laden des Sitemap-Status: ${errorMessage}`);
    } finally {
      setLoadingSitemapStatus(false);
    }
  };

  const handleCheckSitemap = async () => {
    try {
      setCheckingSitemap(true);
      const payload = userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap
        ? { municipality_id: selectedMunicipalityForSitemap }
        : {};
      const year = sitemapConfig.sitemap_sync_config?.exclude_events_before_year;
      if (year != null && year !== '') {
        payload.exclude_events_before_year = typeof year === 'number' ? year : parseInt(year, 10);
      }
      await apiClient.post('/sitemap/check', payload);
      toast.success('Sitemap-Prüfung gestartet');
      // Refresh status after a short delay
      setTimeout(() => {
        fetchSitemapStatus(selectedMunicipalityForSitemap);
      }, 2000);
    } catch (error) {
      console.error('Error checking sitemap:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Fehler beim Prüfen';
      toast.error(`Fehler beim Prüfen der Sitemap: ${errorMessage}`);
    } finally {
      setCheckingSitemap(false);
    }
  };

  const handleSyncSitemap = async () => {
    try {
      setSyncingSitemap(true);
      const payload = userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap
        ? { municipality_id: selectedMunicipalityForSitemap }
        : {};
      const year = sitemapConfig.sitemap_sync_config?.exclude_events_before_year;
      if (year != null && year !== '') {
        payload.exclude_events_before_year = typeof year === 'number' ? year : parseInt(year, 10);
      }
      const response = await apiClient.post('/sitemap/sync', payload);
      const excludeYear = response.data?.exclude_events_before_year;
      const yearText = excludeYear ? ` (Events vor ${excludeYear} werden ausgeschlossen)` : '';
      toast.success(`Sitemap-Synchronisation gestartet${yearText}`);
      // Refresh status after a short delay
      setTimeout(() => {
        fetchSitemapStatus(selectedMunicipalityForSitemap);
      }, 2000);
    } catch (error) {
      console.error('Error syncing sitemap:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Fehler beim Synchronisieren';
      toast.error(`Fehler beim Synchronisieren der Sitemap: ${errorMessage}`);
    } finally {
      setSyncingSitemap(false);
    }
  };

  const fetchSitemapConfig = async () => {
    try {
      setLoadingSitemapConfig(true);
      const params = userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap
        ? { municipality_id: selectedMunicipalityForSitemap }
        : {};
      const response = await apiClient.get('/municipality/sitemap-config', { params });
      setSitemapConfig({
        sitemap_url: response.data.sitemap_url || '',
        sitemap_sync_config: response.data.sitemap_sync_config || {
          enabled: false,
          check_interval_minutes: 60,
          daily_sync_time: '03:00',
          exclude_events_before_year: null
        }
      });
    } catch (error) {
      console.error('Error fetching sitemap config:', error);
      // Don't show error toast on 404 (config might not exist yet)
      if (error.response?.status !== 404) {
        toast.error('Fehler beim Laden der Sitemap-Konfiguration');
      }
    } finally {
      setLoadingSitemapConfig(false);
    }
  };

  // -- BAY:entry ingestion (Provider Admin, Sitemap tab) --
  const startBayEntryIngest = async () => {
    if (!selectedMunicipalityForSitemap) {
      toast.error('Bitte wählen Sie eine Gemeinde aus');
      return;
    }
    try {
      const response = await apiClient.post('/provider/bay-entry/ingest', {
        municipality_id: selectedMunicipalityForSitemap
      });
      const jobId = response.data.job_id;
      setBayEntryJobId(jobId);
      setBayEntryStatus(response.data);
      setBayEntryRunning(true);
      sessionStorage.setItem('bayEntryJobId', jobId);
      sessionStorage.setItem('bayEntryMunicipalityId', selectedMunicipalityForSitemap);
      toast.success(`BAY:entry Import gestartet (${response.data.total_urls} URLs). Job: ${jobId.substring(0, 8)}...`);
      pollBayEntryStatus(jobId);
      if (bayEntryPollIntervalRef.current) clearInterval(bayEntryPollIntervalRef.current);
      bayEntryPollIntervalRef.current = setInterval(() => pollBayEntryStatus(jobId), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Starten des BAY:entry Imports');
    }
  };

  const pollBayEntryStatus = async (jobId) => {
    if (!jobId) return;
    try {
      const response = await apiClient.get(`/provider/bay-entry/ingest/${jobId}`);
      const data = response.data;
      setBayEntryStatus(data);
      if (data.status === 'completed' || data.status === 'failed') {
        setBayEntryRunning(false);
        if (bayEntryPollIntervalRef.current) {
          clearInterval(bayEntryPollIntervalRef.current);
          bayEntryPollIntervalRef.current = null;
        }
        sessionStorage.removeItem('bayEntryJobId');
        sessionStorage.removeItem('bayEntryMunicipalityId');
        if (data.status === 'completed') {
          toast.success(`BAY:entry Import abgeschlossen: ${data.success} erfolgreich, ${data.failed || 0} fehlgeschlagen, ${data.low_content || 0} wenig Inhalt`);
        } else {
          toast.error('BAY:entry Import fehlgeschlagen');
        }
      }
    } catch (e) {
      if (e.response?.status === 404) {
        setBayEntryRunning(false);
        setBayEntryJobId(null);
        setBayEntryStatus(null);
        if (bayEntryPollIntervalRef.current) {
          clearInterval(bayEntryPollIntervalRef.current);
          bayEntryPollIntervalRef.current = null;
        }
        sessionStorage.removeItem('bayEntryJobId');
        sessionStorage.removeItem('bayEntryMunicipalityId');
      }
      console.error('Error polling BAY:entry status:', e);
    }
  };

  const downloadBayEntryReport = (format) => {
    if (!bayEntryStatus || !bayEntryJobId) return;
    const summary = {
      job_id: bayEntryStatus.job_id,
      municipality_id: bayEntryStatus.municipality_id,
      status: bayEntryStatus.status,
      total: bayEntryStatus.total,
      success: bayEntryStatus.success ?? 0,
      failed: bayEntryStatus.failed ?? 0,
      low_content: bayEntryStatus.low_content ?? 0,
      started_at: bayEntryStatus.started_at,
    };
    const errors = bayEntryStatus.errors || [];
    const lowContent = bayEntryStatus.low_content_urls || [];
    const results = bayEntryStatus.results || [];

    if (format === 'json') {
      const report = { summary, errors, low_content: lowContent, successful: results };
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bay-entry-report-${bayEntryJobId.substring(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const rows = [
        ['url', 'title', 'status', 'error', 'chunks', 'doc_id'].join(','),
        ...errors.map((e) => [
          `"${(e.url || '').replace(/"/g, '""')}"`,
          `"${(e.title || '').replace(/"/g, '""')}"`,
          'failed',
          `"${(e.error || '').replace(/"/g, '""')}"`,
          '',
          '',
        ].join(',')),
        ...lowContent.map((r) => [
          `"${(r.url || '').replace(/"/g, '""')}"`,
          `"${(r.title || '').replace(/"/g, '""')}"`,
          'low_content',
          '',
          String(r.chunks ?? ''),
          (r.doc_id || ''),
        ].join(',')),
        ...results.map((r) => [
          `"${(r.url || '').replace(/"/g, '""')}"`,
          `"${(r.title || '').replace(/"/g, '""')}"`,
          r.status || 'ok',
          '',
          String(r.chunks ?? ''),
          (r.doc_id || ''),
        ].join(',')),
      ];
      const csv = rows.join('\r\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bay-entry-report-${bayEntryJobId.substring(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSaveSitemapConfig = async (e) => {
    e.preventDefault();
    try {
      setSavingSitemapConfig(true);
      const payload = {
        sitemap_url: sitemapConfig.sitemap_url || null,
        sitemap_sync_config: sitemapConfig.sitemap_sync_config || null
      };
      if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap) {
        payload.municipality_id = selectedMunicipalityForSitemap;
      }
      await apiClient.patch('/municipality/sitemap-config', payload);
      toast.success('Sitemap-Konfiguration erfolgreich gespeichert');
      // Refresh status after save
      fetchSitemapStatus(selectedMunicipalityForSitemap);
    } catch (error) {
      console.error('Error saving sitemap config:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Fehler beim Speichern';
      toast.error(`Fehler beim Speichern: ${errorMessage}`);
    } finally {
      setSavingSitemapConfig(false);
    }
  };

  // -- Reindex Functions (Provider Admin) --
  const handleStartReindex = async () => {
    if (!selectedMunicipalityForSitemap) {
      toast.error('Bitte wählen Sie eine Gemeinde aus');
      return;
    }
    
    try {
      setReindexRunning(true);
      setReindexJob(null);
      
      const payload = {
        municipality_id: selectedMunicipalityForSitemap,
        source: 'sitemap',
        exclude_events_before_year: reindexForm.exclude_events_before_year || null,
        deactivate_old_chunks: reindexForm.deactivate_old_chunks,
        dry_run: false,
        max_urls: reindexForm.max_urls || null
      };
      
      const response = await apiClient.post('/provider/index/reindex', payload);
      const jobId = response.data.job_id;
      
      toast.success('Reindex gestartet');
      
      // Start polling for job status
      pollReindexStatus(jobId);
      
    } catch (error) {
      console.error('Error starting reindex:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Starten';
      toast.error(`Fehler: ${errorMessage}`);
      setReindexRunning(false);
    }
  };

  const pollReindexStatus = async (jobId) => {
    try {
      const response = await apiClient.get(`/provider/index/reindex/${jobId}`);
      const job = response.data;
      setReindexJob(job);
      
      if (job.status === 'running' || job.status === 'pending') {
        // Continue polling
        setTimeout(() => pollReindexStatus(jobId), 1500);
      } else {
        // Job finished
        setReindexRunning(false);
        if (job.status === 'completed') {
          toast.success(`Reindex abgeschlossen: ${job.counters?.docs_created || 0} Dokumente, ${job.counters?.chunks_created || 0} Chunks`);
          // Refresh index health after reindex completes
          if (selectedMunicipalityForSitemap) {
            fetchIndexHealth(selectedMunicipalityForSitemap);
          }
        } else if (job.status === 'failed') {
          toast.error(`Reindex fehlgeschlagen: ${job.error || 'Unbekannter Fehler'}`);
        } else if (job.status === 'cancelled') {
          toast.info('Reindex wurde abgebrochen');
          // Refresh index health after cancel
          if (selectedMunicipalityForSitemap) {
            fetchIndexHealth(selectedMunicipalityForSitemap);
          }
        }
      }
    } catch (error) {
      console.error('Error polling reindex status:', error);
      setReindexRunning(false);
    }
  };

  const handleCancelReindex = async () => {
    if (!reindexJob?.job_id) {
      toast.error('Kein laufender Reindex-Job');
      return;
    }
    
    try {
      await apiClient.post(`/provider/index/reindex/${reindexJob.job_id}/cancel`);
      toast.info('Reindex wird abgebrochen...');
    } catch (error) {
      console.error('Error cancelling reindex:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Abbrechen';
      toast.error(`Fehler: ${errorMessage}`);
    }
  };

  const handleRetryFailed = async () => {
    if (!reindexJob?.job_id || !reindexJob?.failed_urls?.length) {
      toast.error('Keine fehlgeschlagenen URLs zum Wiederholen');
      return;
    }
    
    try {
      setReindexRunning(true);
      const response = await apiClient.post(`/provider/index/reindex/${reindexJob.job_id}/retry-failed`);
      const newJobId = response.data.new_job_id;
      
      toast.success(`Retry gestartet für ${response.data.urls_to_retry} URLs`);
      
      // Start polling the new job
      pollReindexStatus(newJobId);
      
    } catch (error) {
      console.error('Error retrying failed URLs:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Retry';
      toast.error(`Fehler: ${errorMessage}`);
      setReindexRunning(false);
    }
  };

  const handleDownloadReport = async (format) => {
    if (!reindexJob?.job_id) {
      toast.error('Kein Reindex-Job vorhanden');
      return;
    }
    
    try {
      const response = await apiClient.get(`/provider/index/reindex/${reindexJob.job_id}/report`, {
        params: { format },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reindex-report-${reindexJob.job_id.substring(0, 8)}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Report als ${format.toUpperCase()} heruntergeladen`);
    } catch (error) {
      console.error('Error downloading report:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Download';
      toast.error(`Fehler: ${errorMessage}`);
    }
  };

  const handleDownloadSyncReport = async (runId, format) => {
    try {
      const response = await apiClient.get(`/sitemap/sync/${runId}/report`, {
        params: { format },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sitemap-sync-report-${runId.substring(0, 8)}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Sync-Report als ${format.toUpperCase()} heruntergeladen`);
    } catch (error) {
      console.error('Error downloading sync report:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Download';
      toast.error(`Fehler: ${errorMessage}`);
    }
  };

  const handlePurgeInactiveChunks = async () => {
    if (!selectedMunicipalityForSitemap) {
      toast.error('Bitte wählen Sie eine Gemeinde aus');
      return;
    }
    
    try {
      setPurgeRunning(true);
      setShowPurgeConfirm(false);
      
      const response = await apiClient.delete('/provider/index/purge', {
        data: {
          municipality_id: selectedMunicipalityForSitemap,
          older_than_days: purgeForm.older_than_days
        }
      });
      
      const deletedCount = response.data.deleted_chunks;
      toast.success(`${deletedCount} inaktive Chunks gelöscht`);
      
      // Refresh index health after purge completes
      if (selectedMunicipalityForSitemap) {
        fetchIndexHealth(selectedMunicipalityForSitemap);
      }
      
    } catch (error) {
      console.error('Error purging chunks:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Löschen';
      toast.error(`Fehler: ${errorMessage}`);
    } finally {
      setPurgeRunning(false);
    }
  };

  const fetchRetentionStatus = async () => {
    try {
      const response = await apiClient.get('/provider/index/retention-status');
      setRetentionStatus(response.data);
    } catch (error) {
      console.error('Error fetching retention status:', error);
    }
  };

  // -- Index Health Functions (Provider Admin) --
  const fetchIndexHealth = async (municipalityId) => {
    if (!municipalityId) return;
    
    try {
      setLoadingIndexHealth(true);
      const response = await apiClient.get('/provider/index/health', {
        params: { municipality_id: municipalityId }
      });
      setIndexHealth(response.data);
    } catch (error) {
      console.error('Error fetching index health:', error);
      setIndexHealth(null);
    } finally {
      setLoadingIndexHealth(false);
    }
  };

  // -- Dirty URLs (Provider Admin) --
  const fetchDirtyReport = async () => {
    const municipalityId = selectedMunicipalityForSitemap;
    if (!municipalityId) {
      toast.error('Bitte zuerst eine Gemeinde auswählen.');
      return;
    }
    try {
      setLoadingDirtyReport(true);
      setDirtyReport(null);
      const response = await apiClient.get('/provider/index/dirty-report', {
        params: { municipality_id: municipalityId }
      });
      setDirtyReport(response.data);
      toast.success(`Dirty-Report: ${response.data.total_dirty_documents} Dokumente, ${response.data.total_dirty_chunks} dirty Chunks`);
    } catch (error) {
      console.error('Error fetching dirty report:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Laden des Dirty-Reports');
      setDirtyReport(null);
    } finally {
      setLoadingDirtyReport(false);
    }
  };

  const pollReingestStatus = async (jobId, municipalityId) => {
    try {
      const response = await apiClient.get(`/provider/index/reindex/${jobId}`);
      const job = response.data;
      setReingestJob(job);

      if (job.status === 'running' || job.status === 'pending') {
        setTimeout(() => pollReingestStatus(jobId, municipalityId), 1500);
      } else {
        setReingestRunning(false);
        if (job.status === 'completed') {
          const ok = job.counters?.urls_processed ?? 0;
          const failed = job.failed_urls?.length ?? 0;
          toast.success(`Re-Ingest abgeschlossen: ${ok} OK, ${failed} fehlgeschlagen`);
          fetchIndexHealth(municipalityId);
          fetchDirtyReport();
          setDirtyReport(null);
        } else if (job.status === 'failed') {
          toast.error(`Re-Ingest fehlgeschlagen: ${job.error || 'Unbekannter Fehler'}`);
        }
      }
    } catch (error) {
      console.error('Error polling reingest status:', error);
      setReingestRunning(false);
      toast.error(error.response?.data?.detail || 'Fehler beim Abrufen des Status');
    }
  };

  const handleReingestDirty = async () => {
    const municipalityId = selectedMunicipalityForSitemap;
    if (!dirtyReport?.dirty_documents?.length) {
      toast.error('Keine dirty URLs zum Re-Ingest. Zuerst Dirty-Report laden.');
      return;
    }
    if (!window.confirm(`Alle ${dirtyReport.dirty_documents.length} dirty URLs neu ingestieren (strategy=replace)? Das kann einige Minuten dauern.`)) {
      return;
    }
    try {
      setReingestRunning(true);
      setReingestJob({ job_id: null, status: 'pending', progress: 0, logs_tail: [] });
      const urls = dirtyReport.dirty_documents.map((d) => d.url);
      const response = await apiClient.post('/provider/index/reingest-dirty', {
        municipality_id: municipalityId,
        urls
      });
      const jobId = response.data.job_id;
      setReingestJob((prev) => (prev ? { ...prev, job_id: jobId } : { job_id: jobId, status: 'pending', progress: 0, logs_tail: [] }));
      toast.success('Re-Ingest gestartet');
      pollReingestStatus(jobId, municipalityId);
    } catch (error) {
      console.error('Error reingesting dirty URLs:', error);
      setReingestRunning(false);
      setReingestJob(null);
      toast.error(error.response?.data?.detail || 'Fehler beim Re-Ingest');
    }
  };

  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) {
      setSelectedMunicipalityForSitemap(selectedMunicipalityId);
    }
  }, [selectedMunicipalityId]);

  // Fetch sitemap status when municipality changes (for ProviderAdmin) or on mount (for MunicipalityAdmin)
  useEffect(() => {
    if (userInfo && (userInfo.role === 'ProviderAdmin' || userInfo.role === 'MunicipalityAdmin')) {
      if (userInfo.role === 'ProviderAdmin' && selectedMunicipalityForSitemap) {
        fetchSitemapStatus(selectedMunicipalityForSitemap);
        fetchSitemapConfig();
      } else if (userInfo.role === 'MunicipalityAdmin') {
        fetchSitemapStatus();
        fetchSitemapConfig();
      }
    }
  }, [userInfo, selectedMunicipalityForSitemap]);

  // Fetch retention status and index health when provider admin views indexing section
  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap) {
      fetchRetentionStatus();
      fetchIndexHealth(selectedMunicipalityForSitemap);
    }
  }, [userInfo, selectedMunicipalityForSitemap]);

  // Restore running BAY:entry ingest job on mount (Provider Admin)
  useEffect(() => {
    if (!userInfo || userInfo.role !== 'ProviderAdmin') return;
    const savedJobId = sessionStorage.getItem('bayEntryJobId');
    if (!savedJobId) return;
    if (bayEntryJobId === savedJobId && bayEntryPollIntervalRef.current) return;
    setBayEntryJobId(savedJobId);
    setBayEntryRunning(true);
    if (bayEntryPollIntervalRef.current) {
      clearInterval(bayEntryPollIntervalRef.current);
      bayEntryPollIntervalRef.current = null;
    }
    pollBayEntryStatus(savedJobId);
    const interval = setInterval(() => pollBayEntryStatus(savedJobId), 2000);
    bayEntryPollIntervalRef.current = interval;
    return () => {
      clearInterval(interval);
      bayEntryPollIntervalRef.current = null;
    };
  }, [userInfo?.role]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Sitemap-Verwaltung</h2>
        <Button 
          variant="outline" 
          onClick={() => fetchSitemapStatus(selectedMunicipalityForSitemap)}
          disabled={loadingSitemapStatus}
          className={isDark 
            ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white" 
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
          }
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingSitemapStatus ? 'animate-spin' : ''}`} />
          Status aktualisieren
        </Button>
      </div>

      {/* Municipality Selector for ProviderAdmin */}
      {userInfo?.role === 'ProviderAdmin' && (
        <Card className={isDark 
          ? "bg-slate-900 border-slate-800 shadow-lg" 
          : "bg-white border-slate-200 shadow-lg"
        }>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
              <Label className={isDark ? 'text-slate-200' : 'text-slate-700'}>Kommune auswählen:</Label>
              <Select 
                value={selectedMunicipalityForSitemap || ''} 
                onValueChange={(val) => {
                  setSelectedMunicipalityForSitemap(val);
                  setSitemapStatus(null); // Clear status when changing municipality
                  setSitemapConfig({
                    sitemap_url: '',
                    sitemap_sync_config: {
                      enabled: false,
                      check_interval_minutes: 60,
                      daily_sync_time: '03:00'
                    }
                  }); // Clear config when changing municipality
                  setIndexHealth(null); // Clear index health when changing municipality
                  setReindexJob(null); // Clear reindex job when changing municipality
                  setReingestJob(null);
                  setBayEntryJobId(null);
                  setBayEntryStatus(null);
                  setBayEntryRunning(false);
                }}
              >
                <SelectTrigger className={`w-[300px] ${
                  isDark 
                    ? 'bg-slate-950 border-slate-700 text-white' 
                    : 'bg-white border-slate-300 text-slate-900'
                }`}>
                  <SelectValue placeholder="Bitte Kommune auswählen" />
                </SelectTrigger>
                <SelectContent className={isDark 
                  ? 'bg-slate-900 border-slate-700 text-white' 
                  : 'bg-white border-slate-300 text-slate-900'
                }>
                  {municipalitiesList.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BAY:entry ingestion (Provider Admin only) */}
      {userInfo?.role === 'ProviderAdmin' && (
        <Card className={isDark 
          ? "bg-slate-900 border-slate-800 shadow-lg" 
          : "bg-white border-slate-200 shadow-lg"
        }>
          <CardHeader>
            <CardTitle className={isDark ? "text-white" : "text-slate-900"}>BAY:entry Seiten importieren</CardTitle>
            <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              Importiert alle BAY:entry Service-Seiten aus dem Portal-Verzeichnis in die Wissensdatenbank (fetch_url pro URL).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={startBayEntryIngest}
              disabled={!selectedMunicipalityForSitemap || bayEntryRunning}
              className={isDark 
                ? "bg-sky-600 hover:bg-sky-500 text-white" 
                : "bg-sky-600 hover:bg-sky-700 text-white"
              }
            >
              {bayEntryRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import läuft...
                </>
              ) : (
                'Import starten'
              )}
            </Button>
            {bayEntryRunning && bayEntryStatus && (
              <div className={`text-sm rounded-lg p-3 ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                <div className="font-medium">Fortschritt</div>
                <div>
                  {bayEntryStatus.completed} / {bayEntryStatus.total} URLs — 
                  Erfolgreich: {bayEntryStatus.success ?? 0}, 
                  Fehlgeschlagen: {bayEntryStatus.failed ?? 0}, 
                  Wenig Inhalt: {bayEntryStatus.low_content ?? 0}
                </div>
                {bayEntryStatus.progress != null && (
                  <div className="mt-2 h-2 rounded-full overflow-hidden bg-slate-700">
                    <div 
                      className="h-full bg-sky-500 transition-all" 
                      style={{ width: `${Math.round((bayEntryStatus.progress || 0) * 100)}%` }} 
                    />
                  </div>
                )}
              </div>
            )}
            {!bayEntryRunning && bayEntryStatus && (bayEntryStatus.status === 'completed' || bayEntryStatus.status === 'failed') && (
              <>
                <div className={`text-sm rounded-lg p-3 ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                  Letzter Lauf: {bayEntryStatus.success ?? 0} erfolgreich, {bayEntryStatus.failed ?? 0} fehlgeschlagen, {bayEntryStatus.low_content ?? 0} wenig Inhalt
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadBayEntryReport('json')}
                    className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Report (JSON)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => downloadBayEntryReport('csv')}
                    className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Report (CSV)
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBayEntryReportExpanded((e) => !e)}
                    className={isDark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}
                  >
                    {bayEntryReportExpanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    Bericht anzeigen
                  </Button>
                </div>
                {bayEntryReportExpanded && (
                  <div className={`text-sm rounded-lg border p-3 space-y-3 max-h-80 overflow-y-auto ${isDark ? 'bg-slate-950 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                    {(bayEntryStatus.errors?.length > 0) && (
                      <div>
                        <div className="font-semibold text-red-500 dark:text-red-400">Fehlgeschlagen ({bayEntryStatus.errors.length})</div>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                          {bayEntryStatus.errors.map((e, i) => (
                            <li key={i} className="truncate" title={e.error}>
                              <a href={e.url} target="_blank" rel="noopener noreferrer" className="underline">{e.title || e.url}</a>
                              <span className="block text-xs opacity-80">{e.error}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(bayEntryStatus.low_content_urls?.length > 0) && (
                      <div>
                        <div className="font-semibold text-amber-600 dark:text-amber-400">Wenig Inhalt ({bayEntryStatus.low_content_urls.length})</div>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                          {bayEntryStatus.low_content_urls.map((r, i) => (
                            <li key={i}>
                              <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline">{r.title || r.url}</a>
                              <span className="text-xs opacity-80"> — {r.chunks ?? 0} Chunks</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(bayEntryStatus.results?.length > 0) && (
                      <div>
                        <div className="font-semibold text-green-600 dark:text-green-400">Erfolgreich ({bayEntryStatus.results.length})</div>
                        <ul className="list-disc list-inside mt-1 space-y-0.5">
                          {bayEntryStatus.results.slice(0, 20).map((r, i) => (
                            <li key={i}>
                              <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline">{r.title || r.url}</a>
                              <span className="text-xs opacity-80"> — {r.chunks ?? 0} Chunks{r.status === 'duplicate' ? ' (Duplikat)' : ''}</span>
                            </li>
                          ))}
                          {bayEntryStatus.results.length > 20 && (
                            <li className="text-xs opacity-80">… und {bayEntryStatus.results.length - 20} weitere</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sitemap Configuration Form */}
      <Card className={isDark 
        ? "bg-slate-900 border-slate-800 shadow-lg" 
        : "bg-white border-slate-200 shadow-lg"
      }>
        <CardHeader>
          <CardTitle className={isDark ? "text-white" : "text-slate-900"}>Sitemap-Konfiguration</CardTitle>
          <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
            Konfigurieren Sie die Sitemap-URL und Synchronisationseinstellungen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {userInfo?.role === 'ProviderAdmin' && !selectedMunicipalityForSitemap ? (
            <div className={`h-32 flex items-center justify-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              <span className="text-sm">Bitte wählen Sie eine Gemeinde aus, um die Sitemap-Konfiguration anzuzeigen.</span>
            </div>
          ) : loadingSitemapConfig ? (
            <div className={`h-32 flex items-center justify-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Lade Konfiguration...
            </div>
          ) : (
            <form onSubmit={handleSaveSitemapConfig} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sitemap_url">Sitemap URL</Label>
                <Input
                  id="sitemap_url"
                  type="url"
                  value={sitemapConfig.sitemap_url}
                  onChange={e => setSitemapConfig({...sitemapConfig, sitemap_url: e.target.value})}
                  placeholder="https://example.com/sitemap.xml"
                  className={isDark 
                    ? "bg-slate-950 border-slate-700 text-white" 
                    : "bg-white border-slate-300 text-slate-900"
                  }
                />
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Vollständige URL zur Sitemap (muss HTTPS sein)
                </p>
              </div>

              <div className={`space-y-3 pt-2 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Synchronisationseinstellungen</Label>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="sync_enabled" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Synchronisation aktivieren</Label>
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      Aktiviert automatische Sitemap-Synchronisation
                    </p>
                  </div>
                  <input
                    id="sync_enabled"
                    type="checkbox"
                    checked={sitemapConfig.sitemap_sync_config?.enabled || false}
                    onChange={e => setSitemapConfig({
                      ...sitemapConfig,
                      sitemap_sync_config: {
                        ...sitemapConfig.sitemap_sync_config,
                        enabled: e.target.checked
                      }
                    })}
                    className={`w-5 h-5 rounded border-2 ${
                      isDark 
                        ? 'bg-slate-950 border-slate-600 text-sky-500' 
                        : 'bg-white border-slate-300 text-sky-600'
                    } focus:ring-2 focus:ring-sky-500`}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="exclude_events_before_year">Events vor Jahr ausschließen</Label>
                  <Input
                    id="exclude_events_before_year"
                    type="number"
                    min="2024"
                    max="2100"
                    value={sitemapConfig.sitemap_sync_config?.exclude_events_before_year || ''}
                    onChange={e => setSitemapConfig({
                      ...sitemapConfig,
                      sitemap_sync_config: {
                        ...sitemapConfig.sitemap_sync_config,
                        exclude_events_before_year: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                    placeholder="2024"
                    className={isDark 
                      ? "bg-slate-950 border-slate-700 text-white" 
                      : "bg-white border-slate-300 text-slate-900"
                    }
                  />
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Events vor diesem Jahr werden beim Sitemap-Sync nicht indexiert (Minimum: 2024). Leer = verwendet letzter Reindex-Wert oder Standard (2024).
                  </p>
                </div>

                {sitemapConfig.sitemap_sync_config?.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="check_interval_minutes">Prüfintervall (Minuten)</Label>
                      <Input
                        id="check_interval_minutes"
                        type="number"
                        min="1"
                        value={sitemapConfig.sitemap_sync_config.check_interval_minutes || 60}
                        onChange={e => setSitemapConfig({
                          ...sitemapConfig,
                          sitemap_sync_config: {
                            ...sitemapConfig.sitemap_sync_config,
                            check_interval_minutes: parseInt(e.target.value) || 60
                          }
                        })}
                        className={isDark 
                          ? "bg-slate-950 border-slate-700 text-white" 
                          : "bg-white border-slate-300 text-slate-900"
                        }
                      />
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Wie oft soll die Sitemap auf Änderungen geprüft werden?
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="daily_sync_time">Tägliche Synchronisationszeit (HH:MM)</Label>
                      <Input
                        id="daily_sync_time"
                        type="text"
                        value={sitemapConfig.sitemap_sync_config.daily_sync_time || '03:00'}
                        onChange={e => setSitemapConfig({
                          ...sitemapConfig,
                          sitemap_sync_config: {
                            ...sitemapConfig.sitemap_sync_config,
                            daily_sync_time: e.target.value
                          }
                        })}
                        placeholder="03:00"
                        pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                        className={isDark 
                          ? "bg-slate-950 border-slate-700 text-white" 
                          : "bg-white border-slate-300 text-slate-900"
                        }
                      />
                      <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        Uhrzeit für die tägliche Synchronisation (Format: HH:MM, z.B. 03:00)
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  type="submit" 
                  disabled={savingSitemapConfig} 
                  className="bg-sky-600 hover:bg-sky-500 text-white"
                >
                  {savingSitemapConfig ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Speichere...
                    </>
                  ) : (
                    'Speichern'
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Sitemap Status and Actions */}
      <Card className={isDark 
        ? "bg-slate-900 border-slate-800 shadow-lg" 
        : "bg-white border-slate-200 shadow-lg"
      }>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>Status & Aktionen</CardTitle>
          <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-600'}>
            Sitemap prüfen und synchronisieren
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingSitemapStatus ? (
            <div className={`h-32 flex items-center justify-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Lade Status...
            </div>
          ) : (
            <>
              {/* Status Display */}
              {sitemapStatus && (
                <div className={`space-y-3 p-4 rounded-lg border ${
                  isDark 
                    ? 'bg-slate-950 border-slate-700' 
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div>
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Sitemap URL:</Label>
                    <p className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>
                      {sitemapStatus.sitemap_url || 'Nicht konfiguriert'}
                    </p>
                  </div>
                  {sitemapStatus.sitemap_sync_config?.exclude_events_before_year && (
                    <div>
                      <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Event-Filter:</Label>
                      <p className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>
                        Events vor {sitemapStatus.sitemap_sync_config.exclude_events_before_year} werden ausgeschlossen
                      </p>
                    </div>
                  )}
                  {sitemapStatus.sitemap_sync_status && (
                    <div className="space-y-2">
                      <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Letzte Prüfung:</Label>
                      <p className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-900'}`}>
                        {sitemapStatus.sitemap_sync_status.last_check_at 
                          ? new Date(sitemapStatus.sitemap_sync_status.last_check_at).toLocaleString('de-DE')
                          : 'Noch nie'}
                      </p>
                      {sitemapStatus.sitemap_sync_status.pending && (
                        <div className={`flex items-center gap-2 p-2 rounded ${isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-50 text-yellow-800'}`}>
                          <Check className="w-4 h-4" />
                          <span className="text-sm">Ausstehende Änderungen vorhanden</span>
                        </div>
                      )}
                      {sitemapStatus.sitemap_sync_status.pending_counts && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Hinzugefügt: </span>
                            <span className={isDark ? 'text-green-300' : 'text-green-700'}>{sitemapStatus.sitemap_sync_status.pending_counts.added || 0}</span>
                          </div>
                          <div>
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Geändert: </span>
                            <span className={isDark ? 'text-yellow-300' : 'text-yellow-700'}>{sitemapStatus.sitemap_sync_status.pending_counts.changed || 0}</span>
                          </div>
                          <div>
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Entfernt: </span>
                            <span className={isDark ? 'text-red-300' : 'text-red-700'}>{sitemapStatus.sitemap_sync_status.pending_counts.removed || 0}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleCheckSitemap}
                  disabled={checkingSitemap || (userInfo?.role === 'ProviderAdmin' && !selectedMunicipalityForSitemap)}
                  className="bg-sky-600 hover:bg-sky-500 text-white flex-1"
                >
                  {checkingSitemap ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Prüfe...
                    </>
                  ) : (
                    <>
                      <MapIcon className="w-4 h-4 mr-2" />
                      Sitemap jetzt prüfen
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleSyncSitemap}
                  disabled={syncingSitemap || (userInfo?.role === 'ProviderAdmin' && !selectedMunicipalityForSitemap)}
                  className="bg-green-600 hover:bg-green-500 text-white flex-1"
                >
                  {syncingSitemap ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Synchronisiere...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sitemap jetzt synchronisieren
                    </>
                  )}
                </Button>
              </div>

              {/* Runs History */}
              {sitemapStatus && sitemapStatus.runs && sitemapStatus.runs.length > 0 && (
                <div className="mt-4">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Letzte Ausführungen:</Label>
                  <div className={`mt-2 space-y-2 max-h-64 overflow-y-auto ${isDark ? 'bg-slate-950' : 'bg-slate-50'} p-3 rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    {sitemapStatus.runs.map((run, idx) => (
                      <div key={idx} className={`text-sm p-2 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                        <div className="flex justify-between items-center">
                          <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                            {run.run_type === 'check' ? 'Prüfung' : 'Synchronisation'} ({run.triggered_by === 'manual' ? 'Manuell' : 'Automatisch'})
                          </span>
                          <div className="flex items-center gap-2">
                            {run.run_type === 'sync' && run._id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadSyncReport(run._id, 'csv')}
                                className={`h-6 px-2 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'}`}
                                title="Report herunterladen (CSV)"
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            )}
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                              {new Date(run.started_at).toLocaleString('de-DE')}
                            </span>
                          </div>
                        </div>
                        {run.status && (
                          <Badge className={`mt-1 ${
                            run.status === 'completed' || run.status === 'success'
                              ? 'bg-green-600' 
                              : run.status === 'failed'
                              ? 'bg-red-600'
                              : 'bg-yellow-600'
                          }`}>
                            {run.status === 'completed' ? 'Abgeschlossen' : run.status === 'completed_with_errors' ? 'Mit Fehlern' : run.status}
                          </Badge>
                        )}
                        {run.counts && (
                          <div className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {run.run_type === 'check' ? (
                              <>
                                Hinzugefügt: {run.counts.added || 0}, 
                                Geändert: {run.counts.changed || 0}, 
                                Entfernt: {run.counts.removed || 0}
                                {run.counts.filtered_event_old > 0 && `, Gefiltert: ${run.counts.filtered_event_old}`}
                              </>
                            ) : (
                              <>
                                Indexiert: {run.counts.indexed || 0}, 
                                Fehler: {run.counts.failed || 0}
                                {run.counts.filtered_event_old > 0 && `, Gefiltert: ${run.counts.filtered_event_old}`}
                              </>
                            )}
                          </div>
                        )}
                        {run.logs_tail && run.logs_tail.length > 0 && (
                          <div className={`mt-2 text-xs ${isDark ? 'bg-slate-800' : 'bg-slate-100'} p-2 rounded max-h-32 overflow-y-auto font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            {run.logs_tail.map((log, logIdx) => (
                              <div key={logIdx}>{log}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* INDEXING SECTION (Provider Admin only) */}
      {userInfo?.role === 'ProviderAdmin' && selectedMunicipalityForSitemap && (
        <Card className={`mt-6 ${isDark 
          ? "bg-slate-900 border-slate-800 shadow-lg" 
          : "bg-white border-slate-200 shadow-lg"
        }`}>
          <CardHeader>
            <CardTitle className={isDark ? "text-white" : "text-slate-900"}>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Indexing (Provider Admin)
              </div>
            </CardTitle>
            <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              Vollständige Neuindexierung und Bereinigung der Wissensbasis.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Index Health Section */}
            <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Index Health
                  </div>
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchIndexHealth(selectedMunicipalityForSitemap)}
                  disabled={loadingIndexHealth}
                  className={isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingIndexHealth ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {loadingIndexHealth ? (
                <div className={`flex items-center justify-center h-24 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Lade Health-Status...
                </div>
              ) : indexHealth ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* Active Chunks */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Aktive Chunks
                    </div>
                    <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {indexHealth.chunks_active?.toLocaleString('de-DE') || 0}
                    </div>
                  </div>
                  
                  {/* Inactive Chunks */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Inaktive Chunks
                    </div>
                    <div className={`text-2xl font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      {indexHealth.chunks_inactive?.toLocaleString('de-DE') || 0}
                    </div>
                  </div>
                  
                  {/* Retention Days */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Retention
                    </div>
                    <div className={`text-2xl font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                      {indexHealth.retention_days || 30} <span className="text-sm font-normal">Tage</span>
                    </div>
                  </div>
                  
                  {/* Last Reindex */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Letzter Reindex
                    </div>
                    <div className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                      {indexHealth.last_reindex_at 
                        ? new Date(indexHealth.last_reindex_at).toLocaleDateString('de-DE', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Noch nie</span>
                      }
                    </div>
                    {indexHealth.last_reindex_job_id && (
                      <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'} truncate`}>
                        Job: {indexHealth.last_reindex_job_id.substring(0, 8)}...
                      </div>
                    )}
                  </div>
                  
                  {/* Last Cleanup */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Letzter Cleanup
                    </div>
                    <div className={`text-sm ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                      {indexHealth.last_cleanup_run_at 
                        ? new Date(indexHealth.last_cleanup_run_at).toLocaleDateString('de-DE', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Noch nie</span>
                      }
                    </div>
                    {indexHealth.deleted_last_cleanup > 0 && (
                      <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {indexHealth.deleted_last_cleanup} gelöscht
                      </div>
                    )}
                  </div>
                  
                  {/* Total Chunks */}
                  <div className={`p-3 rounded ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                    <div className={`text-xs uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Gesamt
                    </div>
                    <div className={`text-2xl font-bold ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>
                      {((indexHealth.chunks_active || 0) + (indexHealth.chunks_inactive || 0)).toLocaleString('de-DE')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  Keine Health-Daten verfügbar
                </div>
              )}
            </div>
            
            {/* Full Reindex Section */}
            <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
              <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Vollständige Neuindexierung
              </h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Holt alle URLs aus der Sitemap und indexiert sie neu. Bestehende Chunks werden deaktiviert.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                    Events vor Jahr ausschließen
                  </Label>
                  <Input
                    type="number"
                    min="2000"
                    max="2100"
                    value={reindexForm.exclude_events_before_year || ''}
                    onChange={e => setReindexForm({
                      ...reindexForm, 
                      exclude_events_before_year: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="z.B. 2024"
                    className={isDark 
                      ? "bg-slate-900 border-slate-700 text-white" 
                      : "bg-white border-slate-300 text-slate-900"
                    }
                  />
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    Veranstaltungs-URLs mit älterem Jahr werden übersprungen
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                    Optionen
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="deactivate_old_chunks"
                      checked={reindexForm.deactivate_old_chunks}
                      onChange={e => setReindexForm({...reindexForm, deactivate_old_chunks: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="deactivate_old_chunks" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Alte Chunks deaktivieren
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="show_advanced"
                      checked={reindexForm.showAdvanced}
                      onChange={e => setReindexForm({...reindexForm, showAdvanced: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="show_advanced" className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Erweiterte Optionen
                    </Label>
                  </div>
                </div>
              </div>
              
              {reindexForm.showAdvanced && (
                <div className="mb-4 space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                    Max. URLs (für Tests)
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="50000"
                    value={reindexForm.max_urls || ''}
                    onChange={e => setReindexForm({
                      ...reindexForm, 
                      max_urls: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="Leer = alle URLs"
                    className={`max-w-xs ${isDark 
                      ? "bg-slate-900 border-slate-700 text-white" 
                      : "bg-white border-slate-300 text-slate-900"
                    }`}
                  />
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  onClick={handleStartReindex}
                  disabled={reindexRunning}
                  className="bg-sky-600 hover:bg-sky-500 text-white"
                >
                  {reindexRunning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Reindex läuft...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Vollständig neu indexieren
                    </>
                  )}
                </Button>
                
                {reindexRunning && (
                  <Button
                    onClick={handleCancelReindex}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Abbrechen
                  </Button>
                )}
              </div>
              
              {/* Reindex Job Status */}
              {reindexJob && (
                <div className={`mt-4 p-3 rounded border ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                      Status: {reindexJob.status === 'running' ? 'Läuft' : 
                               reindexJob.status === 'completed' ? 'Abgeschlossen' : 
                               reindexJob.status === 'failed' ? 'Fehlgeschlagen' : 
                               reindexJob.status === 'cancelled' ? 'Abgebrochen' :
                               reindexJob.status}
                    </span>
                    <Badge className={
                      reindexJob.status === 'completed' ? 'bg-green-600' :
                      reindexJob.status === 'failed' ? 'bg-red-600' :
                      reindexJob.status === 'running' ? 'bg-yellow-600' :
                      reindexJob.status === 'cancelled' ? 'bg-orange-600' :
                      'bg-slate-600'
                    }>
                      {Math.round(reindexJob.progress * 100)}%
                    </Badge>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className={`w-full h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        reindexJob.status === 'completed' ? 'bg-green-500' :
                        reindexJob.status === 'failed' ? 'bg-red-500' :
                        reindexJob.status === 'cancelled' ? 'bg-orange-500' :
                        'bg-sky-500'
                      }`}
                      style={{ width: `${reindexJob.progress * 100}%` }}
                    />
                  </div>
                  
                  {/* Counters */}
                  {reindexJob.counters && (
                    <div className={`mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      <div>URLs: {reindexJob.counters.urls_processed}/{reindexJob.counters.urls_total}</div>
                      <div>Dokumente: {reindexJob.counters.docs_created}</div>
                      <div>Chunks: {reindexJob.counters.chunks_created}</div>
                      <div>Übersprungen: {(reindexJob.counters.urls_skipped_event_old || 0) + (reindexJob.counters.urls_skipped_domain || 0)}</div>
                    </div>
                  )}
                  
                  {/* Logs */}
                  {reindexJob.logs_tail && reindexJob.logs_tail.length > 0 && (
                    <div className={`mt-3 max-h-32 overflow-y-auto text-xs font-mono p-2 rounded ${isDark ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                      {reindexJob.logs_tail.map((log, idx) => (
                        <div key={idx}>{log}</div>
                      ))}
                    </div>
                  )}
                  
                  {reindexJob.error && (
                    <div className="mt-2 text-sm text-red-500">
                      Fehler: {reindexJob.error}
                    </div>
                  )}
                  
                  {/* Failed URLs section */}
                  {reindexJob.failed_urls && reindexJob.failed_urls.length > 0 && (
                    <div className={`mt-3 p-2 rounded border ${isDark ? 'border-orange-800 bg-orange-950/30' : 'border-orange-200 bg-orange-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                          {reindexJob.failed_urls.length} Fehlgeschlagene URLs
                        </span>
                        {(reindexJob.status === 'completed' || reindexJob.status === 'failed' || reindexJob.status === 'cancelled') && !reindexRunning && (
                          <Button
                            size="sm"
                            onClick={handleRetryFailed}
                            className="bg-orange-600 hover:bg-orange-500 text-white text-xs"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Retry Failed
                          </Button>
                        )}
                      </div>
                      <div className={`max-h-32 overflow-y-auto text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {reindexJob.failed_urls.slice(0, 10).map((f, idx) => (
                          <div key={idx} className="truncate py-0.5">
                            <span className={isDark ? 'text-orange-400' : 'text-orange-600'}>{f.url.split('/').pop()}</span>
                            <span className="ml-2 opacity-60">- {f.error?.substring(0, 50)}...</span>
                          </div>
                        ))}
                        {reindexJob.failed_urls.length > 10 && (
                          <div className="py-1 opacity-60">
                            ... und {reindexJob.failed_urls.length - 10} weitere
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Minimal indexed info */}
                  {reindexJob.counters?.urls_indexed_minimal > 0 && (
                    <div className={`mt-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      ℹ️ {reindexJob.counters.urls_indexed_minimal} Dokumente wurden mit minimalem Inhalt (nur Titel) indexiert
                    </div>
                  )}
                  
                  {/* Download Report Buttons */}
                  {(reindexJob.status === 'completed' || reindexJob.status === 'failed' || reindexJob.status === 'cancelled') && (
                    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          Report herunterladen:
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadReport('csv')}
                          className={`text-xs ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          CSV
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadReport('pdf')}
                          className={`text-xs ${isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Dirty URLs bereinigen (Re-ingest dirty docs) */}
            <div className={`p-4 rounded-lg border ${isDark ? 'border-slate-700 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
              <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                <RefreshCw className="w-4 h-4" />
                Dirty URLs bereinigen
              </h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Findet Chunks mit unrenderten Templates (Mustache, sa2search, „Suchbegriff eingeben") und ermöglicht gezieltes Re-Ingest nur dieser URLs (strategy=replace).
              </p>
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={fetchDirtyReport}
                  disabled={loadingDirtyReport || !selectedMunicipalityForSitemap}
                  variant="outline"
                  className={isDark ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}
                >
                  {loadingDirtyReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Dirty-Report laden
                </Button>
                {dirtyReport?.dirty_documents?.length > 0 && (
                  <Button
                    onClick={handleReingestDirty}
                    disabled={reingestRunning}
                    className="bg-amber-600 hover:bg-amber-500 text-white"
                  >
                    {reingestRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Alle {dirtyReport.dirty_documents.length} re-ingestieren
                  </Button>
                )}
              </div>
              {reingestJob && (
                <div className={`mb-4 rounded-lg border p-3 ${isDark ? 'border-amber-800 bg-amber-950/30' : 'border-amber-200 bg-amber-50'}`}>
                  <p className={`text-sm font-medium mb-2 ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                    {reingestJob.status === 'running' || reingestJob.status === 'pending'
                      ? 'Re-Ingest läuft…'
                      : reingestJob.status === 'completed'
                        ? 'Re-Ingest abgeschlossen'
                        : reingestJob.status === 'failed'
                          ? 'Re-Ingest fehlgeschlagen'
                          : 'Re-Ingest'}
                  </p>
                  <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-full bg-amber-500 transition-all duration-300"
                      style={{ width: `${Math.round((reingestJob.progress ?? 0) * 100)}%` }}
                    />
                  </div>
                  {reingestJob.logs_tail?.length > 0 && (
                    <div className={`max-h-40 overflow-y-auto rounded border p-2 font-mono text-xs ${isDark ? 'border-slate-700 bg-slate-900 text-slate-300' : 'border-slate-200 bg-white text-slate-700'}`}>
                      {reingestJob.logs_tail.map((line, idx) => (
                        <div key={idx} className="whitespace-pre-wrap break-all">{line}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {dirtyReport && (
                <div className="space-y-2">
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {dirtyReport.total_dirty_documents} Dokumente, {dirtyReport.total_dirty_chunks} dirty Chunks (von {dirtyReport.total_chunks_scanned} gescannt).
                  </p>
                  {dirtyReport.dirty_documents?.length > 0 ? (
                    <div className={`max-h-48 overflow-y-auto rounded border p-2 ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                            <th className="text-left py-1">Titel</th>
                            <th className="text-left py-1">Dirty</th>
                            <th className="text-left py-1">Patterns</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dirtyReport.dirty_documents.slice(0, 30).map((d, idx) => (
                            <tr key={d.document_id} className={isDark ? 'text-slate-300 border-slate-800' : 'text-slate-700 border-slate-100'} style={{ borderBottomWidth: 1 }}>
                              <td className="py-1 truncate max-w-[200px]" title={d.title}>{d.title}</td>
                              <td className="py-1">{d.count_dirty_chunks}/{d.total_chunks}</td>
                              <td className="py-1 truncate max-w-[120px]">{d.matched_patterns?.join(', ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {dirtyReport.dirty_documents.length > 30 && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          … und {dirtyReport.dirty_documents.length - 30} weitere
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      Keine dirty Chunks gefunden.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Purge Section (Danger Zone) */}
            <div className={`p-4 rounded-lg border-2 ${isDark ? 'border-red-900 bg-red-950/20' : 'border-red-200 bg-red-50'}`}>
              <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                <Trash2 className="w-4 h-4" />
                Inaktive Chunks löschen (Danger Zone)
              </h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Löscht dauerhaft deaktivierte Chunks aus der Datenbank. Standard-Aufbewahrung: 30 Tage.
              </p>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                    Älter als (Tage)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={purgeForm.older_than_days}
                    onChange={e => setPurgeForm({...purgeForm, older_than_days: parseInt(e.target.value) || 0})}
                    className={`w-32 ${isDark 
                      ? "bg-slate-900 border-slate-700 text-white" 
                      : "bg-white border-slate-300 text-slate-900"
                    }`}
                  />
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    0 = alle inaktiven Chunks sofort löschen
                  </p>
                </div>
                
                <Button
                  onClick={() => setShowPurgeConfirm(true)}
                  disabled={purgeRunning}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-500"
                >
                  {purgeRunning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Lösche...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Inaktive Chunks löschen
                    </>
                  )}
                </Button>
              </div>
              
              {/* Purge Confirmation Dialog */}
              {showPurgeConfirm && (
                <div className={`mt-4 p-4 rounded border-2 ${isDark ? 'border-red-800 bg-red-950' : 'border-red-300 bg-red-100'}`}>
                  <p className={`font-medium mb-3 ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                    ⚠️ Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                  <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Es werden alle deaktivierten Chunks gelöscht, die älter als {purgeForm.older_than_days} Tage sind.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handlePurgeInactiveChunks}
                      variant="destructive"
                      size="sm"
                    >
                      Ja, endgültig löschen
                    </Button>
                    <Button
                      onClick={() => setShowPurgeConfirm(false)}
                      variant="outline"
                      size="sm"
                      className={isDark ? 'border-slate-600' : ''}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Retention Info */}
            <div className={`p-3 rounded border ${isDark ? 'border-slate-700 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
              <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                <p className="font-medium mb-2">ℹ️ Automatischer Cleanup</p>
                {retentionStatus ? (
                  <div className="space-y-1">
                    <p>
                      • Aufbewahrungsfrist: <span className="font-medium">{retentionStatus.retention_days} Tage</span>
                    </p>
                    <p>
                      • Status: {retentionStatus.chunk_cleanup_enabled ? (
                        <span className="text-green-500">Aktiviert</span>
                      ) : (
                        <span className="text-red-500">Deaktiviert</span>
                      )}
                    </p>
                    {retentionStatus.last_cleanup_run_at && (
                      <p>
                        • Letzter Cleanup: {new Date(retentionStatus.last_cleanup_run_at).toLocaleString('de-DE')}
                        {retentionStatus.deleted_last_run !== null && (
                          <span className="ml-1">({retentionStatus.deleted_last_run} gelöscht)</span>
                        )}
                      </p>
                    )}
                    {!retentionStatus.last_cleanup_run_at && (
                      <p className={isDark ? 'text-slate-500' : 'text-slate-500'}>
                        • Noch kein automatischer Cleanup ausgeführt
                      </p>
                    )}
                  </div>
                ) : (
                  <p>Inaktive Chunks werden nach 30 Tagen automatisch gelöscht (täglich um 03:00 Uhr).</p>
                )}
              </div>
            </div>
            
          </CardContent>
        </Card>
      )}
    </div>
  );
}
